import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { appSettings } from './appSettings';

// ============================================================================
// Types
// ============================================================================

export interface ApiRequestConfig extends AxiosRequestConfig {
  skipAuth?: boolean;
}

export interface AuthenticatedApiClient {
  get<T = any>(url: string, config?: ApiRequestConfig): Promise<AxiosResponse<T>>;
  post<T = any>(url: string, data?: any, config?: ApiRequestConfig): Promise<AxiosResponse<T>>;
  put<T = any>(url: string, data?: any, config?: ApiRequestConfig): Promise<AxiosResponse<T>>;
  delete<T = any>(url: string, config?: ApiRequestConfig): Promise<AxiosResponse<T>>;
  patch<T = any>(url: string, data?: any, config?: ApiRequestConfig): Promise<AxiosResponse<T>>;
}

// ============================================================================
// API Client Configuration
// ============================================================================

const api: AxiosInstance = axios.create({
  baseURL: appSettings.API_BASE_URL,
  timeout: 300000, // 5 minutes - increased for slow queries
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================================================
// Authenticated API Factory
// ============================================================================

export const createAuthenticatedApi = (token: string): AuthenticatedApiClient => {
  const getAuthHeaders = (config: ApiRequestConfig = {}): AxiosRequestConfig => ({
    ...config,
    headers: {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    },
  });

  return {
    get: <T = any>(url: string, config?: ApiRequestConfig) =>
      api.get<T>(url, getAuthHeaders(config)),

    post: <T = any>(url: string, data?: any, config?: ApiRequestConfig) =>
      api.post<T>(url, data, getAuthHeaders(config)),

    put: <T = any>(url: string, data?: any, config?: ApiRequestConfig) =>
      api.put<T>(url, data, getAuthHeaders(config)),

    delete: <T = any>(url: string, config?: ApiRequestConfig) =>
      api.delete<T>(url, getAuthHeaders(config)),

    patch: <T = any>(url: string, data?: any, config?: ApiRequestConfig) =>
      api.patch<T>(url, data, getAuthHeaders(config)),
  };
};

// ============================================================================
// Exports
// ============================================================================

export { api };
export default api;